import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/browser';
import ErrorBoundary from 'components/ErrorBoundary';
import Maintenance from 'components/Maintenance';
import { useRouterEvents } from 'hooks/ui/useRouterEvents';
import { DefaultSeo } from 'next-seo';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { RootStoreProvider } from 'providers/RootStoreProvider';
import { useEffect, useState } from 'react';
import ScriptLayer from 'scripts/ScriptLayer';
import { overrideAnalyticsEnabled } from 'utils/analytics/analytics';
import {
  GOOGLE_CLIENT_ID,
  MAINTENANCE_MODE,
  runningInProduction,
} from 'utils/constants';
import { setCookiesFromQueryParams } from 'utils/setCookiesFromQueryParams';
import '../asset/css/global.css';
import LoadingOverlay from '../components/layout/LoadingOverlay';
import SEO from '../next-seo.config';
import '../public/montserrat/montserrat.css';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import './about-us.globals.css';

Sentry.init({
  environment: runningInProduction ? 'production' : 'development',
  // A `null` DNS is the official way of disabling the SDK.
  dsn: runningInProduction
    ? 'https://ef2866d0a06245d99d69e16c683dc94c@o386295.ingest.sentry.io/5220387'
    : 'https://5da9b79462deafe41d5900e5dbb9c237@o4506536506753024.ingest.sentry.io/4506536508588032',
  ignoreErrors: [
    'adsbygoogle.push() error: No slot size for availableWidth=0', // Ads work fine, so this (very common) error is not helpful
  ],
});

const reportSentryError = (error: any, errorInfo: any) => {
  Sentry.withScope((scope) => {
    Object.keys(errorInfo).forEach((key) => {
      scope.setExtra(key, errorInfo[key]);
    });

    Sentry.captureException(error);
  });
};

export default function App({ Component, pageProps }: AppProps<any>) {
  const router = useRouter();

  const [generatedDate, _setGeneratedDate] = useState(() => {
    // We want a date on every page to help with debugging caching. We don't want _app.tsx to require a
    // getServerSideProps or getStaticProps as this will cause NextJS to treat every page as server-side rendered.
    // Instead, on the client-side let's fetch the value out of the server-side generated DOM and use that. On the
    // server-side we use the current date. When running in development, the hot-reloading means there won't be a
    // server-side rendered HTML, so just use 'development'
    if (process.env.NODE_ENV === 'development') {
      return 'development';
    }
    if (typeof window !== 'undefined') {
      const dateInDom = document
        .querySelector('#daymade-page-info')
        ?.innerHTML.trim();
      return dateInDom;
    } else {
      new Date().toISOString();
    }
  });
  const [loading, setLoading] = useState(false);
  useRouterEvents(router, setLoading);

  useEffect(() => {
    const emailAddress = localStorage['login_email_address'] || '';

    if (emailAddress !== '') {
      Sentry.setUser({
        email: emailAddress,
      });
    }

    setCookiesFromQueryParams(router.query);
  }, [router.query, router.isReady]);

  if (MAINTENANCE_MODE) {
    return <Maintenance />;
  }
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <RootStoreProvider hydrationData={pageProps.hydrationData}>
        <ErrorBoundary reportSentryError={reportSentryError}>
          {loading && <LoadingOverlay />}
          <DefaultSeo {...SEO} />
          {(runningInProduction || overrideAnalyticsEnabled) && <ScriptLayer />}
          {router.isReady && <Component {...pageProps} />}
          {/* Output the time this page was generated. This can help with seeing what pages are cached */}
          <div id="daymade-page-info" data-generated={generatedDate} />
        </ErrorBoundary>
      </RootStoreProvider>
    </GoogleOAuthProvider>
  );
}
